import type { TableName } from "./entities.js";

/**
 * Wire protocol for the data gateway.
 *
 * The Supabase build used PostgREST's query-builder vocabulary throughout the UI.
 * Keeping that vocabulary — as an explicit, typed request object rather than a URL
 * grammar — let the ~100 call sites in the app carry over unchanged while the
 * gateway translates each request into DynamoDB Query/Scan/Transact calls.
 */

export type FilterOperator =
  "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "like" | "ilike" | "in" | "is" | "contains";

export interface Filter {
  column: string;
  op: FilterOperator;
  value: unknown;
  /** Inverts the match, as PostgREST's `.not(column, op, value)` does. */
  negate?: boolean;
}

export interface OrderBy {
  column: string;
  ascending: boolean;
}

export interface SelectRequest {
  table: TableName;
  /** Column projection. `*` or omitted returns the whole row. */
  select?: string[];
  filters?: Filter[];
  /** OR group: a row matches if any filter in the group matches. */
  or?: Filter[];
  order?: OrderBy[];
  limit?: number;
  offset?: number;
  /** Ask for an exact row count alongside the page. */
  count?: boolean;
  /** Return the count only, with no rows. */
  head?: boolean;
  /** "single" errors unless exactly one row matches; "maybe" allows zero. */
  cardinality?: "many" | "single" | "maybe";
}

export interface MutateRequest {
  table: TableName;
  op: "insert" | "update" | "upsert" | "delete";
  /** Rows for insert/upsert, or the patch for update. */
  values?: Record<string, unknown> | Record<string, unknown>[];
  /** Which rows an update/delete applies to. */
  filters?: Filter[];
  /** Conflict target for upsert; defaults to the table's primary key. */
  onConflict?: string[];
  /** On upsert, leave an existing row untouched instead of updating it. */
  ignoreDuplicates?: boolean;
  /** Return the affected rows in the response. */
  returning?: boolean;
}

/**
 * Paging.
 *
 * A list endpoint that answers with the whole table is a bug that only shows up
 * once the table is big: the Lambda holds every row in memory, the response
 * grows without bound, and the browser parses thousands of rows to paint twenty.
 * So a page is the default answer and `limit` is capped — a caller that wants
 * more than one page asks for the next one rather than for all of them.
 *
 * `MAX_PAGE_SIZE` is the ceiling rather than a second default: the option lists
 * behind the dropdowns genuinely do want every row, and they say so by asking
 * for exactly this many. That keeps those reads visible at the call site and
 * still bounded, where no limit at all was neither.
 */

/** Page size a list endpoint uses when the caller does not name one. */
export const DEFAULT_PAGE_SIZE = 10;

/** Ceiling on one page. A `limit` above this is refused, not silently trimmed. */
export const MAX_PAGE_SIZE = 1000;

/** The sizes the page-size picker offers; the first is the smallest allowed. */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

/**
 * Where the returned rows sit in the matching set.
 *
 * `total` counts the rows the filters match, not the rows in the page, which is
 * what a "1–50 of 328" footer and a page count both need. It is exact: the
 * gateway has already filtered the candidate rows in memory by the time it cuts
 * the window, so counting them costs nothing extra.
 */
export interface PageMeta {
  /** Rows matching the filters, before the page window is applied. */
  total: number;
  /** The window actually used, after the default and the cap. */
  limit: number;
  offset: number;
  /** 1-based page number, derived from `offset` and `limit`. */
  page: number;
  pageCount: number;
  hasMore: boolean;
}

export function pageMeta(total: number, limit: number, offset: number): PageMeta {
  // A zero limit would divide by zero below; it means "no rows", which is page 1
  // of a set whose pages are all empty.
  const size = Math.max(1, limit);
  return {
    total,
    limit,
    offset,
    page: Math.floor(offset / size) + 1,
    pageCount: Math.max(1, Math.ceil(total / size)),
    hasMore: offset + limit < total,
  };
}

export interface DataError {
  message: string;
  code?: string;
  details?: string;
}

export interface DataResponse<T> {
  data: T | null;
  error: DataError | null;
  count?: number | null;
  /** Present on a list response; absent on a read, a write or an error. */
  page?: PageMeta | null;
}

export const DATA_SELECT_PATH = "/data/select";
export const DATA_MUTATE_PATH = "/data/mutate";
